import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface FeedWidgetConfig {
  new_members: boolean;
  member_spotlight: boolean;
  upcoming_events: boolean;
  community_pulse: boolean;
}

const DEFAULT_CONFIG: FeedWidgetConfig = {
  new_members: true,
  member_spotlight: true,
  upcoming_events: true,
  community_pulse: true,
};

export function useFeedWidgets() {
  return useQuery({
    queryKey: ['feed-widgets'],
    queryFn: async (): Promise<FeedWidgetConfig> => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'feed_widgets')
        .maybeSingle();

      if (error) throw error;
      if (!data?.value) return DEFAULT_CONFIG;
      return { ...DEFAULT_CONFIG, ...(data.value as Partial<FeedWidgetConfig>) };
    },
  });
}

export function useUpdateFeedWidgets() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: FeedWidgetConfig) => {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('app_settings')
        .update({
          value: config as unknown as Record<string, unknown>,
          updated_by: user?.id || null,
          updated_at: new Date().toISOString(),
        })
        .eq('key', 'feed_widgets');

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed-widgets'] });
    },
  });
}
